import React from 'react'
import {
	Box,
	Paper,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Typography,
} from '@mui/material'
import { AppCardTitle, AppEmptyLine } from '@components'
import { dsColorNew } from '@palette'
import { MB_CHART_COLORS } from 'Pages/MyBusiness/_shared/Ui'
import { PieChart } from '@mui/x-charts/PieChart'

/**
 * Channel mix breakdown for the Revenue dashboard.
 *
 *   ┌──────────────┬──────────────────────────────┐
 *   │              │  Channel | Bookings | Rev …  │
 *   │   Pie chart  │  OTA     | 1 234    | 200K   │
 *   │   (share %)  │  Direct  |   980    | 150K   │
 *   │              │  ...                  ▼ scroll │
 *   └──────────────┴──────────────────────────────┘
 *
 * Rows come from `rows: [{channel_type, bookings, revenue, share_pct, …}]`.
 * Click on a row (or a pie slice) fires `onChannel(channel_type)` so the
 * caller can open the Partner drill-down dialog.
 */
const PALETTE = MB_CHART_COLORS

const fmtNum = (n) => new Intl.NumberFormat('es-ES').format(n)

export default function ChannelMix({
	title = 'Share by Channel',
	rows = [],
	valueKey = 'revenue',
	valueFormatter = (n) => fmtNum(n),
	onChannel,
}) {
	const data = rows.map((r, i) => ({
		id:    r.channel_type,
		label: r.channel_type,
		value: Number(r[valueKey] || 0),
		color: PALETTE[i % PALETTE.length],
		_row:  r,
	}))

	return (
		<Paper sx={{ p: 2.5 }}>
			<AppCardTitle icon="shuffle" title={title}/>

			<Box display="flex" gap={2} alignItems="stretch" sx={{ mt: 1, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
				{/* Pie */}
				<Box sx={{ flex: '0 0 320px', minHeight: 280,
				           display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
					{data.length === 0 ? (
						<AppEmptyLine>No channel data in window.</AppEmptyLine>
					) : (
						<PieChart
							series={[{
								data,
								innerRadius: 50,
								outerRadius: 110,
								paddingAngle: 1,
								cornerRadius: 3,
								highlightScope: { fade: 'global', highlight: 'item' },
								faded: { innerRadius: 50, additionalRadius: -8, color: dsColorNew.textSubtle },
								valueFormatter: (v) => valueFormatter(v.value),
							}]}
							onItemClick={onChannel ? (_, item) => {
								const ch = data[item.dataIndex]?._row?.channel_type
								if (ch) onChannel(ch)
							} : undefined}
							width={300} height={280}
							margin={{ top: 8, bottom: 8, left: 8, right: 8 }}
							slotProps={{ legend: { hidden: true } }}
						/>
					)}
				</Box>

				{/* Table */}
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<TableContainer sx={{ maxHeight: 320 }}>
						<Table size="small" stickyHeader>
							<TableHead>
								<TableRow>
									<TableCell>Channel</TableCell>
									<TableCell align="right">Bookings</TableCell>
									<TableCell align="right">Revenue</TableCell>
									<TableCell align="right">Share</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{data.length === 0 && (
									<TableRow>
										<TableCell colSpan={4} align="center" sx={{ py: 1 }}>
											<AppEmptyLine>No channel data in window.</AppEmptyLine>
										</TableCell>
									</TableRow>
								)}
								{data.map((d, i) => {
									const r = d._row
									const clickable = typeof onChannel === 'function'
									return (
										<TableRow key={d.id}
										          hover={clickable}
										          onClick={clickable ? () => onChannel(r.channel_type) : undefined}
										          sx={{ cursor: clickable ? 'pointer' : 'default' }}>
											<TableCell>
												<Box display="flex" alignItems="center" gap={1}>
													<Box sx={{ width: 10, height: 10, borderRadius: '2px',
													           bgcolor: d.color, flex: '0 0 10px' }}/>
													<Box sx={{ fontWeight: 600 }}>{r.channel_type}</Box>
												</Box>
											</TableCell>
											<TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
												{fmtNum(r.bookings)}
											</TableCell>
											<TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
												{valueFormatter(r[valueKey] ?? 0)}
											</TableCell>
											<TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
												{r.share_pct != null ? `${r.share_pct.toFixed(1)}%` : '—'}
											</TableCell>
										</TableRow>
									)
								})}
							</TableBody>
						</Table>
					</TableContainer>
					{onChannel && data.length > 0 && (
						<Typography sx={{ fontSize: 12, color: dsColorNew.textMuted, mt: 1, textAlign: 'right' }}>
							Click a row or a slice to see the partners inside.
						</Typography>
					)}
				</Box>
			</Box>
		</Paper>
	)
}
